import React from "react";
import {
  LayoutGrid,
  CalendarRange,
  ClipboardList,
  BookOpen,
  Boxes,
  Truck,
  Users,
  Thermometer,
  BarChart3,
  Search,
  Bell,
  Plus,
} from "lucide-react";

const NAV = [
  { icon: LayoutGrid, label: "Workflow" },
  { icon: CalendarRange, label: "Weekly Menu" },
  { icon: ClipboardList, label: "Production" },
  { icon: BookOpen, label: "Recipes" },
  { icon: Boxes, label: "Inventory" },
  { icon: Truck, label: "Deliveries" },
  { icon: Users, label: "Suppliers" },
  { icon: Thermometer, label: "Daily Checks" },
  { icon: BarChart3, label: "Reports" },
];

const STATUS = {
  "IN PROGRESS": "bg-[#F5E6C6] text-[#976713]",
  READY: "bg-[#D7EFDF] text-[#2C7A4E]",
  CLOSED: "bg-[#EBE7DF] text-[#7F7A71]",
  "NOT PLANNED": "bg-[#EBE7DF] text-[#7F7A71]",
  REVIEWED: "bg-[#D5EAEE] text-[#2A7683]",
  DRAFT: "bg-[#EBE7DF] text-[#7F7A71]",
  CRITICAL: "bg-[#F8DBD1] text-[#BE452A]",
  COMPLETED: "bg-[#D7EFDF] text-[#2C7A4E]",
  PENDING: "bg-[#F5E6C6] text-[#976713]",
  "ACTION REQUIRED": "bg-[#F8DBD1] text-[#BE452A]",
};

const Badge = ({ s }) => (
  <span
    className={`rounded-full px-2 py-[3px] text-[9px] font-semibold uppercase tracking-wide ${
      STATUS[s] || "bg-[#EBE7DF] text-[#7F7A71]"
    }`}
  >
    {s}
  </span>
);

// The MiseOne application window — light, generous, recognisably the real product.
export function ProductWindow({ active = 0, title, subtitle, children, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[var(--mo-line)] bg-white shadow-[0_50px_110px_-45px_rgba(20,18,15,0.4)] ${className}`}
    >
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden w-[176px] shrink-0 flex-col gap-0.5 border-r border-[var(--mo-line)] bg-[#FBFAF6] p-3 md:flex">
          <div className="px-2 pb-3 font-display text-sm font-bold text-[#1b1a18]">
            Mise<span className="text-[var(--mo-orange)]">One</span>
          </div>
          {NAV.map((n, i) => (
            <div
              key={n.label}
              className={`flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[12px] ${
                i === active
                  ? "bg-[#FDEEE7] font-semibold text-[var(--mo-orange)]"
                  : "text-[#6b665e]"
              }`}
            >
              <n.icon size={14} />
              <span>{n.label}</span>
            </div>
          ))}
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          {/* Top bar */}
          <div className="flex items-center justify-between border-b border-[var(--mo-line)] px-5 py-3">
            <div className="font-body text-[11px] uppercase tracking-[0.14em] text-[#9a958c]">
              Restaurant Helsinki · Friday, July 10
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-2 rounded-full border border-[var(--mo-line)] px-3 py-1.5 text-[11px] text-[#9a958c] lg:flex">
                <Search size={12} /> Search…
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--mo-orange)] px-3 py-1.5 text-[11px] font-semibold text-white">
                <Plus size={12} /> Quick Action
              </span>
              <Bell size={15} className="text-[#9a958c]" />
            </div>
          </div>

          {/* Screen header */}
          <div className="px-6 pt-6">
            <div className="font-display text-xl font-bold tracking-tight text-[#1b1a18]">
              {title}
            </div>
            {subtitle && (
              <div className="mt-1 font-body text-[13px] text-[#6b665e]">{subtitle}</div>
            )}
          </div>

          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Real MiseOne product screenshot, framed like an application window.
export function RealScreen({ src, alt, testId = "real-app-screenshot", className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[var(--mo-line)] bg-white shadow-[0_50px_110px_-45px_rgba(20,18,15,0.4)] ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="block h-auto w-full"
        loading="lazy"
        data-testid={testId}
      />
    </div>
  );
}


/* ---- Real screens ------------------------------------------------------- */

const MENU_DAYS = [
  { d: "MON", n: "Jul 7", s: "IN PROGRESS", dishes: [["Crushed Yukon Potatoes", "20 pax"]] },
  { d: "TUE", n: "Jul 8", s: "READY", dishes: [["Chicken Schnitzel, Lemon", "40 pax"]] },
  { d: "WED", n: "Jul 9", s: "NOT PLANNED", dishes: [] },
  { d: "THU", n: "Jul 10", s: "IN PROGRESS", dishes: [["Pan-seared Salmon, Dill Beurre Blanc", "32 pax"]] },
  { d: "FRI", n: "Jul 11", s: "CLOSED", dishes: [] },
  { d: "SAT", n: "Jul 12", s: "NOT PLANNED", dishes: [] },
];

export const WeeklyMenuWindow = ({ className }) => (
  <ProductWindow
    active={1}
    title="Weekly Menu"
    subtitle="What are we serving?"
    className={className}
  >
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      {MENU_DAYS.map((day) => (
        <div key={day.d} className="rounded-lg border border-[var(--mo-line)] bg-[#FCFBF8] p-3.5">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-body text-[11px] font-semibold uppercase tracking-wide text-[#1b1a18]">
              {day.d} <span className="text-[#9a958c]">{day.n}</span>
            </span>
            <Badge s={day.s} />
          </div>
          {day.dishes.length ? (
            day.dishes.map(([name, pax]) => (
              <div key={name} className="mt-2">
                <div className="font-editorial text-[13px] italic leading-snug text-[#1b1a18]">
                  {name}
                </div>
                <div className="mt-0.5 font-body text-[11px] text-[#9a958c]">{pax}</div>
              </div>
            ))
          ) : (
            <div className="mt-3 h-[1px] w-10 bg-[var(--mo-line)]" />
          )}
        </div>
      ))}
    </div>
  </ProductWindow>
);

export const WorkflowWindow = ({ className }) => (
  <ProductWindow
    active={0}
    title="Workflow"
    subtitle="Across Helsinki — what still needs to happen today."
    className={className}
  >
    <div className="mb-5 grid grid-cols-3 gap-3">
      {[
        ["CRITICAL", "1", "bg-[#F8DBD1] text-[#BE452A]"],
        ["MEDIUM", "3", "bg-[#F5E6C6] text-[#976713]"],
        ["LOW", "5", "bg-[#E7EEF0] text-[#5A7A82]"],
      ].map(([label, n, c]) => (
        <div key={label} className="rounded-lg border border-[var(--mo-line)] bg-[#FCFBF8] p-3">
          <div className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-semibold ${c}`}>
            {label}
          </div>
          <div className="mt-2 font-display text-2xl font-bold text-[#1b1a18]">{n}</div>
        </div>
      ))}
    </div>

    <div className="mb-5 grid grid-cols-2 gap-3">
      {[
        ["THIS WEEK", "Planning"],
        ["NEXT WEEK", "Not started"],
      ].map(([t, st]) => (
        <div key={t} className="rounded-lg border border-[var(--mo-line)] bg-[#FCFBF8] p-3.5">
          <div className="font-body text-[10px] uppercase tracking-widest text-[#9a958c]">{t}</div>
          <div className="mt-2 font-editorial text-[15px] italic text-[#1b1a18]">{st}</div>
        </div>
      ))}
    </div>

    <div className="rounded-lg border border-[var(--mo-line)] bg-white p-3.5">
      <div className="mb-3 font-body text-[10px] uppercase tracking-widest text-[#9a958c]">
        Action required
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-body text-[13px] font-medium text-[#1b1a18]">Delivery needs review</div>
          <div className="mt-0.5 font-body text-[11px] text-[#9a958c]">Coastal Fish Co. · 6 items</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge s="CRITICAL" />
          <span className="rounded-full bg-[var(--mo-orange)] px-3 py-1.5 text-[11px] font-semibold text-white">
            Review delivery
          </span>
        </div>
      </div>
    </div>
  </ProductWindow>
);

const CHECKS = [
  ["Fridge Temperatures", "12 of 38 fridges logged", "IN PROGRESS"],
  ["Freezer Temperatures", "6 of 8 freezers logged", "IN PROGRESS"],
  ["Hot Holding Temperature", "17 of 19 logged", "COMPLETED"],
  ["Incoming Product Temp.", "3 entries today", "COMPLETED"],
  ["Cooling Log", "0 entries today", "PENDING"],
  ["Surface Hygiene Tests", "1 overdue", "ACTION REQUIRED"],
];

export const DailyChecksWindow = ({ className }) => (
  <ProductWindow
    active={7}
    title="Daily Checks"
    subtitle="Omavalvonta — have all required food safety checks been completed today?"
    className={className}
  >
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {CHECKS.map(([name, meta, s]) => (
        <div key={name} className="rounded-lg border border-[var(--mo-line)] bg-[#FCFBF8] p-3.5">
          <div className="flex items-start justify-between gap-2">
            <span className="font-body text-[12px] font-semibold text-[#1b1a18]">{name}</span>
            <Badge s={s} />
          </div>
          <div className="mt-2 font-body text-[11px] text-[#9a958c]">{meta}</div>
        </div>
      ))}
    </div>
  </ProductWindow>
);

const METRICS = [
  ["OPENING VALUE", "€ 56.70"],
  ["PHYSICAL INVENTORY", "€ 1.00"],
  ["COUNTED ACCURACY", "100.0%"],
  ["ACTIVE PRODUCTS", "177"],
];

export const ReportsWindow = ({ className }) => (
  <ProductWindow
    active={8}
    title="Reports"
    subtitle="Read-only insight, generated from your operational data."
    className={className}
  >
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {METRICS.map(([label, v]) => (
        <div key={label} className="rounded-lg border border-[var(--mo-line)] bg-[#FCFBF8] p-3.5">
          <div className="font-body text-[9px] uppercase tracking-widest text-[#9a958c]">{label}</div>
          <div className="mt-2 font-display text-lg font-bold text-[#1b1a18]">{v}</div>
        </div>
      ))}
    </div>
    <div className="rounded-lg border border-[var(--mo-line)] bg-white p-4">
      <div className="mb-4 font-body text-[10px] uppercase tracking-widest text-[#9a958c]">
        Counts in period
      </div>
      <div className="flex items-end gap-2.5" style={{ height: 96 }}>
        {[42, 58, 51, 70, 63, 84, 76].map((h, i) => (
          <div key={i} className="flex-1">
            <div className="w-full rounded-t bg-[var(--mo-orange)]/85" style={{ height: `${h}%` }} />
          </div>
        ))}
      </div>
    </div>
  </ProductWindow>
);
